import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCardByToken, signCard } from '../api';

export default function RegcardPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);

  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [signing, setSigning] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [step, setStep] = useState('card');

  useEffect(() => {
    async function load() {
      try {
        const res = await getCardByToken(token);
        setCard(res.data);
        setPhone(res.data.phone || '');
        setEmail(res.data.email || '');
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Ссылка недействительна или истёк срок действия.');
        } else {
          setError('Ошибка загрузки карты. Попробуйте позже.');
        }
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  // Инициализация canvas
  useEffect(() => {
    if (step === 'sign' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [step]);

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }

  function startDraw(e) {
    e.preventDefault();
    isDrawing.current = true;
    const canvas = canvasRef.current;
    const pos = getPos(e, canvas);
    lastPos.current = pos;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = '#1a1a1a';
    ctx.fill();
    setSigEmpty(false);
  }

  function draw(e) {
    e.preventDefault();
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  }

  function endDraw(e) {
    e.preventDefault();
    isDrawing.current = false;
    lastPos.current = null;
  }

  function clearCanvas() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSigEmpty(true);
  }

  async function handleSign() {
    if (sigEmpty) {
      alert('Пожалуйста, поставьте подпись');
      return;
    }
    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signature = canvas.toDataURL('image/png');
      await signCard({
        token,
        signature,
        phone,
        email,
        guestId:       card.guestId,
        guestName:     card.guestName,
        roomStayId:    card.roomStayId,
        hotelId:       card.hotelId,
        hotelName:     card.hotelName,
        bookingNumber: card.bookingNumber,
        citizenship:   card.citizenship,
      });
      navigate('/regcard/success');
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Ссылка истекла. Попросите новый QR-код.');
      } else {
        setError('Ошибка при сохранении. Попробуйте ещё раз.');
      }
    } finally {
      setSigning(false);
    }
  }

  if (loading) return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 16, background: '#f5f5f5'
    }}>
      <div className="spinner" />
      <p style={{ color: '#6b7280' }}>Загрузка карты...</p>
    </div>
  );

  if (error && !card) return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      padding: 24, background: '#f5f5f5'
    }}>
      <div className="card" style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Ссылка недействительна</h2>
        <p style={{ color: '#6b7280', fontSize: 14 }}>{error}</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', paddingBottom: 40 }}>
      <div style={{
        background: '#2563eb', color: '#fff',
        padding: '16px 20px', textAlign: 'center'
      }}>
        <div style={{ fontSize: 14, opacity: 0.85 }}>🏨 {card?.hotelName}</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>Регистрационная карта</div>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '20px 16px' }}>

        {step === 'card' && (
          <>
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Ваши данные</h3>
              <div style={{ marginBottom: 14 }}>
                <label className="label">ФИО</label>
                <div style={{
                  padding: '10px 14px', background: '#f9fafb',
                  borderRadius: 8, fontSize: 15, fontWeight: 500
                }}>
                  {card?.guestName || '—'}
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label className="label">
                  Телефон <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  className="input"
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+7 (___) ___-__-__"
                  style={{ borderColor: !phone.trim() ? '#fca5a5' : undefined }}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label className="label">
                  Email <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  className="input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  style={{ borderColor: !email.trim() ? '#fca5a5' : undefined }}
                />
              </div>
              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
                * Поля обязательны для заполнения
              </p>
            </div>

            <div className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Договор</span>
              </div>
              <div
                style={{
                  padding: 16, fontSize: 12,
                  maxHeight: 320, overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                }}
                dangerouslySetInnerHTML={{ __html: card?.html }}
              />
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <button
              className="btn btn-primary w-full"
              style={{ fontSize: 16, padding: '14px' }}
              onClick={() => {
                if (!phone.trim()) { alert('Пожалуйста, укажите номер телефона'); return; }
                if (!email.trim()) { alert('Пожалуйста, укажите email'); return; }
                setStep('sign');
              }}
            >
              Ознакомлен, перейти к подписи →
            </button>
          </>
        )}

        {step === 'sign' && (
          <>
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Подпись</h3>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                Распишитесь пальцем в поле ниже
              </p>
              <div style={{
                border: '2px solid #d1d5db', borderRadius: 8,
                overflow: 'hidden', background: '#fff',
                touchAction: 'none', userSelect: 'none',
              }}>
                <canvas
                  ref={canvasRef}
                  width={560}
                  height={200}
                  style={{ display: 'block', width: '100%', height: 'auto', touchAction: 'none' }}
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={endDraw}
                />
              </div>
              <button
                className="btn btn-outline btn-sm"
                style={{ marginTop: 10 }}
                onClick={clearCanvas}
              >
                Очистить
              </button>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                className="btn btn-outline"
                style={{ flex: 1 }}
                onClick={() => setStep('card')}
                disabled={signing}
              >
                ← Назад
              </button>
              <button
                className="btn btn-success"
                style={{ flex: 2, fontSize: 16, padding: 14 }}
                onClick={handleSign}
                disabled={signing || sigEmpty}
              >
                {signing ? 'Сохранение...' : '✓ Подписать'}
              </button>
            </div>

            <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 16 }}>
              Нажимая «Подписать», вы подтверждаете согласие с условиями договора
            </p>
          </>
        )}
      </div>
    </div>
  );
}
